import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, ArrowRight, CheckCircle, Building2, 
  Brain, Target, Zap, Users, BarChart3 
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSector, setSelectedSector] = useState(null);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      const response = await fetch('/api/sectors');
      const data = await response.json();
      if (data.success) {
        setSectors(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement secteurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = () => {
    if (selectedSector) {
      navigate(`/questionnaire/${selectedSector}`);
    }
  };

  const benefits = [
    { icon: Brain, title: 'Analyse IA', desc: 'Vos réponses analysées par 3 IA leaders du marché' },
    { icon: Target, title: 'Personnalisé', desc: 'Recommandations adaptées à votre secteur' },
    { icon: Zap, title: 'Rapide', desc: '10 minutes pour un diagnostic complet' },
    { icon: BarChart3, title: 'Actionnable', desc: 'Feuille de route concrète et priorisée' }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#1d1d1f]">Ydv<span className="brand-gradient">Systems</span></span>
            <span className="text-[#d1d1d6]">|</span>
            <span className="text-xl font-bold text-[#636366]">Audit IA</span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-24">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Diagnostic propulsé par l'IA
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Évaluez votre potentiel{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00bcd4] to-[#0891b2]">
              Intelligence Artificielle
            </span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            Questionnaire interactif pour identifier les meilleures opportunités 
            d'intégration de l'IA dans votre entreprise. Diagnostic personnalisé 
            et recommandations actionnables.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm"
              >
                <benefit.icon className="w-5 h-5 text-primary-500" />
                <span className="text-sm font-medium text-gray-700">{benefit.title}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Sector Selection */}
      <section className="max-w-7xl mx-auto px-4 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Sélectionnez votre secteur d'activité
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Pour un diagnostic adapté à vos enjeux métier
          </p>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="loader"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
              {sectors.map((sector, index) => (
                <motion.button
                  key={sector.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedSector(sector.id)}
                  className={`
                    relative p-4 rounded-xl border-2 transition-all duration-200
                    ${selectedSector === sector.id 
                      ? 'border-primary-500 bg-primary-50 shadow-lg scale-105'
                      : 'border-[#e5e5ea] bg-white hover:border-primary-300 hover:shadow-md'}
                  `}
                >
                  {selectedSector === sector.id && (
                    <div className="absolute -top-2 -right-2">
                      <CheckCircle className="w-6 h-6 text-primary-500 fill-white" />
                    </div>
                  )}
                  <div className="text-3xl mb-2">{sector.icon}</div>
                  <div className="text-sm font-medium text-gray-900 leading-tight">
                    {sector.name}
                  </div>
                  {sector.priority === 'high' && (
                    <div className="mt-2 text-xs text-primary-800 font-medium">
                      Forte demande IA
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="flex justify-center">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStart}
              disabled={!selectedSector}
              className={`
                btn btn-primary flex items-center gap-3 text-lg px-8 py-4
                ${!selectedSector ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              Commencer le diagnostic
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* Features */}
      <section className="bg-white border-t border-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-xl font-bold text-center text-gray-900 mb-12">
            Comment ça fonctionne ?
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Répondez au questionnaire',
                desc: 'Questions adaptées à votre secteur, 10 minutes maximum'
              },
              {
                step: '2',
                title: 'Analyse par IA',
                desc: 'Vos réponses analysées par notre intelligence artificielle'
              },
              {
                step: '3',
                title: 'Recevez votre diagnostic',
                desc: 'Score de maturité, opportunités et feuille de route personnalisée'
              }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 bg-primary-100 text-primary-500 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#060608] text-[#a1a1a6] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm">
          <p>© 2026 YdvSystems Audit IA - Propulsé par l'intelligence artificielle</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
